import * as React from 'react';
import { FolderOpen, Clock, ChevronDown, Files } from 'lucide-react';
import { toast } from 'sonner';
import type { CanvasDraft } from '@hector/shared-types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { canvasDraftApi } from '../api/canvasDraft.api';
import { canvasDocumentRuntime } from '../state/canvasDocumentRuntime';
import { useRecentDrafts } from '../hooks/useRecentDrafts';
import { useConversationDrafts } from '../hooks/useConversationDrafts';
import {
  basculerVersLeBrouillon,
  decrireLeRefusDeSortie,
  dependancesDeBasculeParDefaut,
  detacherLeDocumentCourant,
  preparerLaSortieDuDocumentCourant,
} from '../state/canvasBasculeDeBrouillon';
import {
  captureCanvasResidentReplacementTarget,
  replaceCanvasResidentTemplate,
} from '../state/canvasResidentPresentation';
import {
  CANVAS_REPLACEMENT_BLOCKED_HINT,
  CANVAS_REPLACEMENT_BLOCKED_MESSAGE,
  isCanvasSessionProtectedByWork,
} from '../state/canvasSessionProtection';
import { CanvasDraftsList } from './CanvasDraftsList';
import { CanvasDocumentOpenDialog } from './CanvasDocumentOpenDialog';

interface CanvasOpenMenuProps {
  /** Dossier initial du sélecteur du modal « depuis un dossier ». */
  initialCaseId?: string | null;
  /**
   * LE PORTEUR — la conversation dont le document serait remplacé par cette
   * commande. Elle est aussi l'identité de ce document (#1336) : il n'y a plus
   * de second identifiant à passer à côté.
   */
  conversationId: string;
  /**
   * Un tour d'agent rédige-t-il dans le document ouvert ? Les deux entrées de
   * ce menu le **remplacent** — ouvrir pendant une rédaction supprimerait
   * l'acte en cours d'écriture (#893).
   */
  gele: boolean;
}

/**
 * Dropdown « Ouvrir ▾ » du header canvas — trois entrées :
 *  - « Ouvrir depuis un dossier… » → ouvre le modal d'arborescence existant
 *    (`CanvasDocumentOpenDialog`, désormais contrôlé), inchangé ;
 *  - « Documents de cette conversation » → liste inline des autres documents du
 *    fil, avec bascule et suppression (2026-09-22) ;
 *  - « Copier un brouillon d’une autre conversation » → même liste inline, mais
 *    limitée aux AUTRES fils (Tranche 3, 2026-07-21 ; renommée et filtrée le
 *    2026-09-23 : elle s'appelait « Ouvrir un brouillon récent » et montrait
 *    aussi les documents du fil courant, que la copier dupliquait).
 *
 * ⚠️ **Les deux listes se ressemblent et ne font PAS la même chose.** Interne :
 * on BASCULE, en adoptant l'identité du brouillon, donc l'avocat continue de
 * modifier le même document. Transverse : on COPIE le contenu, parce qu'un
 * brouillon ne peut pas changer de conversation (le serveur le refuse) et que
 * son original doit rester chez lui. Confondre les deux ferait accumuler des
 * copies à chaque bascule, jusqu'au plafond, sans que rien ne le signale.
 */
export function CanvasOpenMenu({
  initialCaseId,
  conversationId,
  gele,
}: CanvasOpenMenuProps): React.JSX.Element {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [recentExpanded, setRecentExpanded] = React.useState(false);
  const [filExpanded, setFilExpanded] = React.useState(false);
  // Sans les documents de CE fil : ils se basculent dans « Documents de cette
  // conversation », et les copier ici les dupliquerait (2026-09-23).
  const { drafts: brouillonsDesAutresFils, isLoading } = useRecentDrafts({
    enabled: menuOpen, conversationCourante: conversationId,
  });
  const {
    drafts: draftsDuFil,
    isLoading: chargementDuFil,
    invalider: invaliderLesListes,
  } = useConversationDrafts({ conversationId, enabled: menuOpen });

  // Lu à chaque rendu du menu OUVERT plutôt qu'observé : le contrôleur vit hors
  // de React, et la seule chose qui doit être fraîche ici est ce que la liste
  // affiche à l'instant où l'avocat la déroule.
  const draftOuvertId = menuOpen
    ? canvasDocumentRuntime.getDraftController(conversationId)?.getSnapshot().draftId ?? null
    : null;

  /**
   * Basculer vers un AUTRE document du MÊME fil — on adopte son identité.
   *
   * ⚠️ Ne pas confondre avec `handleSelectDraft` juste en dessous, qui COPIE le
   * contenu d'un brouillon venu d'un autre fil. La distinction est expliquée
   * dans `canvasBasculeDeBrouillon`, qui porte le geste hors de React.
   */
  const handleBasculer = React.useCallback(
    async (draft: CanvasDraft) => {
      setMenuOpen(false);
      const issue = await basculerVersLeBrouillon(
        conversationId,
        draft,
        dependancesDeBasculeParDefaut(),
      );
      switch (issue.issue) {
        case 'bascule':
          toast.success(`« ${draft.title} » est ouvert`);
          return;
        case 'protege':
          toast.error(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
          return;
        case 'montage-remplace':
          toast.error('Le canvas a changé pendant l’ouverture. Réessaie depuis la conversation concernée.');
          return;
        case 'sortant-non-enregistre':
          toast.error(decrireLeRefusDeSortie({ ok: false, raison: 'sortant-non-enregistre', detail: issue.detail }));
          return;
        case 'echec':
          toast.error(issue.detail);
      }
    },
    [conversationId],
  );

  const handleSupprimer = React.useCallback(
    async (draft: CanvasDraft) => {
      try {
        await canvasDraftApi.deleteDraft(draft.id);
        toast.success(`« ${draft.title} » a été supprimé`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Suppression impossible');
      } finally {
        // Même en cas d'échec : le serveur peut avoir supprimé la ligne avant de
        // perdre la réponse, et laisser une entrée fantôme cliquable serait pire
        // qu'un rafraîchissement de trop.
        invaliderLesListes();
      }
    },
    [invaliderLesListes],
  );
  // Un menu déjà déplié quand la rédaction démarre se referme : le laisser
  // ouvert n'offrirait que deux lignes mortes, et l'avocat chercherait ce qu'il
  // a raté. Vaut aussi pour la dialog « depuis un dossier », qui sinon le
  // laisserait naviguer une arborescence entière avant de refuser (#893).
  React.useEffect(() => {
    if (!gele) return;
    setMenuOpen(false);
    setDialogOpen(false);
  }, [gele]);

  const handleSelectDraft = React.useCallback(
    async (draft: CanvasDraft) => {
      setMenuOpen(false);
      // La protection exacte est relue AU MOMENT DU GESTE, hors React : la prop
      // est figée au dernier rendu, et un travail peut démarrer dans un WebSocket —
      // parfois sans geste supplémentaire de l'avocat, quand un signal
      // documentaire reprend après une reconnexion (#893/#1044).
      if (isCanvasSessionProtectedByWork(conversationId)) {
        toast.error(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
        return;
      }
      const target = captureCanvasResidentReplacementTarget({ conversationId });
      try {
        const blob = await canvasDraftApi.getDraftContent(draft.id);
        // RE-CONTRÔLE APRÈS L'ATTENTE : le téléchargement dure, et la rédaction
        // a pu démarrer pendant. Sans ce second appel, la garde d'entrée ne
        // protège que l'instant du clic, pas celui de l'écriture.
        if (isCanvasSessionProtectedByWork(conversationId)) {
          toast.error(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
          return;
        }
        // ⚠️ Le document SORTANT est enregistré et on ATTEND son reçu : le
        // `detacherLeDocumentCourant` plus bas avorte les PUT en vol (défaut
        // corrigé le 2026-09-22). APRÈS le téléchargement, et c'est la dernière
        // attente : une frappe faite pendant celui-ci est enregistrée elle aussi
        // (revue du 2026-09-23). La copie crée un document neuf, donc le plafond
        // du fil est vérifié au passage. Un échec arrête la copie et se dit.
        const sortie = await preparerLaSortieDuDocumentCourant(conversationId, { creeUnDocument: true });
        if (!sortie.ok) {
          toast.error(decrireLeRefusDeSortie(sortie));
          return;
        }
        if (isCanvasSessionProtectedByWork(conversationId)) {
          toast.error(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
          return;
        }
        // On charge via `replaceCanvasResidentTemplate` comme l'import d'une pièce —
        // on ne pose NI l'id dans le contrôleur NI `activeDraft`, réservés au
        // brouillon dont on adopte l'identité (`basculerVersUnBrouillonDuFil`).
        // Le brouillon source `draft.id` vit dans SA conversation et n'est donc
        // jamais écrasé : on en COPIE le contenu.
        //
        // ⚠️ **Ce commentaire affirmait que « l'auto-save repart avec son propre
        // draftId ». C'était FAUX** (corrigé le 2026-09-22) : le remplacement ne
        // touche pas au contrôleur, qui restait sur le document sortant, et la
        // copie l'écrasait au premier enregistrement. C'est le
        // `detacherLeDocumentCourant` plus bas qui rend la phrase vraie.
        //
        // Métadonnées = mirror EXACT de l'hydratation d'un brouillon restauré
        // (RedactionPanel) : documentId = sourceDocumentId ?? null (JAMAIS
        // draft.id — l'agent le rejouerait dans load_document_into_canvas),
        // sourceFilePath null. Delta unique : `kind` threadé pour un badge fidèle
        // (pas d'activeDraft ici pour le porter — cf. draftKind dans RedactionPanel).
        const replacement = replaceCanvasResidentTemplate({
          target,
          blob,
          meta: {
          documentId: draft.sourceDocumentId ?? null,
          documentTitle: draft.title,
          conversationId,
          caseId: draft.caseId,
          sourceFilePath: null,
          kind: draft.kind,
          },
        });
        if (!replacement.accepted) {
          toast.error('Le canvas a changé pendant le chargement du brouillon. Réessaie depuis la conversation concernée.');
          return;
        }
        // ⚠️ **Sans ce détachement, la copie ÉCRASE le document courant**
        // (défaut corrigé le 2026-09-22) : le remplacement ne touche pas au
        // contrôleur, qui pointe encore sur le document sortant, donc le contenu
        // copié partait au prochain enregistrement sous SON identifiant.
        // Détaché, il devient un document NEUF du fil — ce que l'avocat attend
        // d'une copie — et le sortant reste dans « Documents de cette conversation ».
        detacherLeDocumentCourant(conversationId);
        toast.success(`« ${draft.title} » a été copié dans un nouveau document`);
        invaliderLesListes();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Chargement du brouillon impossible');
      }
    },
    [conversationId, invaliderLesListes],
  );

  return (
    <>
      <DropdownMenu
        open={menuOpen}
        // Le refus est ici plutôt que sur chaque entrée : un menu qui s'ouvre
        // pour ne proposer que des lignes mortes fait chercher à l'avocat ce
        // qu'il a raté. L'infobulle du déclencheur, elle, dit pourquoi (#893).
        onOpenChange={(ouvert) => { if (!(ouvert && gele)) setMenuOpen(ouvert); }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                // `aria-disabled`, jamais `disabled` : l'infobulle porte la
                // raison et ne s'ouvrirait pas sur un bouton désactivé (#853).
                className="h-8 gap-1.5 aria-disabled:opacity-60 aria-disabled:cursor-default"
                aria-disabled={gele}
              >
                <FolderOpen className="h-4 w-4" />
                Ouvrir
                <ChevronDown className="h-3.5 w-3.5 text-stone-400" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {`Ouvrir un document dans le canvas${gele ? CANVAS_REPLACEMENT_BLOCKED_HINT : ''}`}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem onSelect={() => setDialogOpen(true)} className="gap-2">
            <FolderOpen className="h-4 w-4 text-stone-500" />
            Ouvrir depuis un dossier…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {/*
            Les documents du fil AVANT les brouillons récents : c'est la liste
            que l'avocat cherche en premier, et les deux entrées ne font pas la
            même chose — ici on bascule (l'identité est adoptée), plus bas on
            copie le contenu d'un document venu d'un autre fil.
          */}
          <DropdownMenuItem
            onSelect={(e) => {
              // preventDefault : garder le menu ouvert pour dérouler la liste inline.
              e.preventDefault();
              setFilExpanded((v) => !v);
            }}
            className="gap-2"
          >
            <Files className="h-4 w-4 text-stone-500" />
            <span className="flex-1">Documents de cette conversation</span>
            <ChevronDown
              className={`h-3.5 w-3.5 text-stone-400 transition-transform ${filExpanded ? 'rotate-180' : ''}`}
            />
          </DropdownMenuItem>
          {filExpanded && (
            <CanvasDraftsList
              drafts={draftsDuFil}
              isLoading={chargementDuFil}
              videLibelle="Aucun autre document dans cette conversation"
              draftOuvertId={draftOuvertId}
              onSelect={(d) => void handleBasculer(d)}
              onDelete={(d) => void handleSupprimer(d)}
            />
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              // preventDefault : garder le menu ouvert pour dérouler la liste inline.
              e.preventDefault();
              setRecentExpanded((v) => !v);
            }}
            className="gap-2"
          >
            <Clock className="h-4 w-4 text-stone-500" />
            <span className="flex-1">Copier un brouillon d’une autre conversation</span>
            <ChevronDown
              className={`h-3.5 w-3.5 text-stone-400 transition-transform ${recentExpanded ? 'rotate-180' : ''}`}
            />
          </DropdownMenuItem>
          {recentExpanded && (
            <CanvasDraftsList
              drafts={brouillonsDesAutresFils}
              isLoading={isLoading}
              videLibelle="Aucun brouillon dans vos autres conversations"
              onSelect={(d) => void handleSelectDraft(d)}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <CanvasDocumentOpenDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialCaseId={initialCaseId}
        conversationId={conversationId}
      />
    </>
  );
}
